import React, { Suspense } from 'react';
import clsx from 'clsx';

import type { SanityHomePageHero } from '#/lib/sanity/queries/sections';
import { useMediaQuery } from '#/lib/useMatchMedia';

import { FormCustom } from '../Form/Custom';
import { Headline } from '../Headline';
import { Link } from '../Link';
import { RichText } from '../RichText';
import { DesktopAnimation } from './HomepageAnimation';
import { MobileAnimation } from './MobileAnimation';

export function HeroHomepage(props: SanityHomePageHero) {
  const isFirst = props.sectionIndex === 0;
  const [matches, hasRun] = useMediaQuery({ screen: 'm' });
  const shouldShowDesktopMedia = matches && hasRun;
  return (
    <div
      className={clsx('bg-charcoal text-cloud text-center m:text-left', {
        'mt-[calc(var(--header-height)*-1)] pb-40 m:pb-100 pt-[calc(var(--header-height)+5rem)]':
          isFirst,
      })}
    >
      <div className="fade-in mx-auto max-w-1500 p-main">
        <div className="flex flex-col l:flex-row gap-30">
          <Headline
            highlightClass="text-electric"
            as="h1"
            variant={1}
            value={props.headline}
            className="w-full l:w-[52%] m:min-w-[530px] max-w-[700px] text-40 m:text-50 l:text-65"
          />
          <div className="flex-1">
            <RichText
              value={props.bodyCopy ?? []}
              normalTextSizeClassName="text-16 font-light leading-140 m:text-20 m:leading-154 m:mb-24 mb-20 text-balance max-w-[600px]"
            />
            {props.form && (
              <FormCustom
                form={props.form}
                confirmation={props.formConfirmation}
                theme="dark"
              />
            )}
            {props.formSubtextLink && (
              <Link
                href={props.formSubtextLink.href}
                className="group text-14  leading-140 text-cloud items-center gap-5 mt-22 transition-all duration-300 hidden m:flex  w-full rounded-full flex-1 py-2 pr-6 max-w-max"
              >
                <svg
                  className="size-20"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.5"
                    y="0.46875"
                    width="17.0615"
                    height="17.0615"
                    rx="8.53076"
                    fill="#55555B"
                  />
                  <path
                    d="M5.08152 9.59615L11.5771 9.59615L8.73929 12.5084C8.5125 12.7411 8.5125 13.123 8.73929 13.3558C8.79309 13.4111 8.85699 13.455 8.92734 13.4849C8.99769 13.5149 9.0731 13.5303 9.14926 13.5303C9.22542 13.5303 9.30084 13.5149 9.37119 13.4849C9.44153 13.455 9.50544 13.4111 9.55924 13.3558L13.3915 9.42309C13.4454 9.36788 13.4881 9.3023 13.5173 9.23011C13.5465 9.15792 13.5615 9.08053 13.5615 9.00237C13.5615 8.92421 13.5465 8.84682 13.5173 8.77463C13.4881 8.70244 13.4454 8.63686 13.3915 8.58165L9.56505 4.64302C9.51121 4.58777 9.4473 4.54394 9.37695 4.51404C9.30661 4.48414 9.23122 4.46875 9.15508 4.46875C9.07894 4.46875 9.00355 4.48414 8.9332 4.51404C8.86286 4.54394 8.79894 4.58777 8.74511 4.64302C8.69127 4.69827 8.64856 4.76386 8.61942 4.83604C8.59029 4.90823 8.57529 4.9856 8.57529 5.06373C8.57529 5.14187 8.59029 5.21924 8.61942 5.29143C8.64856 5.36361 8.69127 5.4292 8.74511 5.48445L11.5771 8.40262L5.08152 8.40262C4.76169 8.40262 4.5 8.67117 4.5 8.99939C4.5 9.3276 4.76169 9.59615 5.08152 9.59615Z"
                    fill="white"
                  />
                </svg>
                <span className="group-hover:border-cloud border-b-[0.5px] border-transparent transition-all duration-300 py-2">
                  {props.formSubtextLink.title}
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className="fade-in media m:mt-70 max-w-screen-1850 mx-auto mt-30 flex-1 p-main">
        {shouldShowDesktopMedia ? <DesktopAnimation /> : <MobileAnimation />}
      </div>
    </div>
  );
}
